import { inject, Injectable } from '@angular/core';
import * as XLSX from "xlsx";
import { saveAs } from 'file-saver';
import { exportDataSet } from '../model/dynamic-data-table.model';
import { CommonUtils } from '../utils/common.utils';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
export const EXCEL_EXTENSION = '.xlsx';
export const CSV_EXTENSION = '.csv';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {
private readonly commonUtils = inject(CommonUtils)
  constructor() { }

  private toExportHeader(key: string): string {
    return this.commonUtils.toCamelCase(key).toUpperCase();
  }
  
formatDynamicData(data: any[]): any[] {
  const formatted: any[] = [];
  data.forEach(record => {
    const mainRow: any = {};
    Object.entries(record).forEach(([col, value]) => {
      const key = this.toExportHeader(col);
      if (Array.isArray(value)) {
        mainRow[key] = value.join(',')
      } else {
        mainRow[key] = value;
      }
    });
    formatted.push(mainRow);
  });
  return formatted;
}
  
  formatDataBeforeExport(dataset:Array<any>, descriptions?:Array<any>, criteriaDataSet?:exportDataSet){
    if(dataset.length <= 0){
      return [];
    }
    const result:any[] = [];
    const criteriaFields = criteriaDataSet?.data
  if(criteriaFields && Object.keys(criteriaFields).length > 0) {
    result.push(['Search Criteria'], ['']);
    Object.entries(criteriaFields).forEach(([key, value])=>{
      result.push( [this.commonUtils.toCamelCase(key), this.commonUtils.toCamelCase(value)])
    })
    result.push(['']);
  }
  //  Add header
  const formattedData = this.formatDynamicData(dataset);
  result.push(Object.keys(formattedData[0]));
  // Add actual data
  Object.values(formattedData).forEach(elm=>{
    result.push(Object.values(elm))
  })
    if(descriptions){
      result.push(['']);
      descriptions.forEach(description=>{
        result.push([description])
      })
    }
    return result
  }
  autofitColumnsForAoa(dataArr: string[][]) {
    const colWidths: XLSX.ColInfo[] = [];
    const numCols = dataArr.reduce((max, row) => Math.max(max, row.length), 0);
    for (let colIndex = 0; colIndex < numCols; colIndex++) {
      let maxLength = 25; // default minimum width
      for (const row of dataArr) {
        const cellValue = row[colIndex];
        if (cellValue) {
          const length = cellValue.toString().length;
          if (length > maxLength && length< 50) {
            maxLength = length;
          }
        }
      }
      colWidths.push({ wch: maxLength + 5 }); // add padding
    }

    return colWidths;
  }
  public exportAsExcelFile(json: any[], excelFileName: string, description?:any, criteriaDataSet?: exportDataSet): void {
    const data = this.formatDataBeforeExport(json, description, criteriaDataSet);
    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);
    const range = XLSX.utils.decode_range(worksheet['!ref'] ?? '100');
    worksheet['!cols'] = this.autofitColumnsForAoa(data);
    for (let C = range.s.r; C <= range.e.r; ++C) { 
      const address = XLSX.utils.encode_col(C) + "1"; // <-- first row, column number C
      if (!worksheet[address]) {
        continue
      };
    }
    
    const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = this.writeWorkbook(workbook);
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }
  
  public exportToMultipleSheets(dataset: any, fileName: string, mergeConfig?: Record<string, string[]>, sheetOrder?: string[]): void {
    const workbook: XLSX.WorkBook = { Sheets: {}, SheetNames: [] };
    
    const sheetNames = sheetOrder || Object.keys(dataset);
    sheetNames.forEach((sheetName) => {
      const data = dataset[sheetName];
      if (!Array.isArray(data) || data.length === 0){
        return
      };

      const rawMergeLabels = mergeConfig?.[sheetName] ?? [];
      const mergeColumnHeaders = rawMergeLabels.map(label => this.toExportHeader(label));

      const formattedData = this.formatDynamicData(data);
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(formattedData);
      this.centerAlignMergeColumns(worksheet, formattedData, mergeColumnHeaders);
      this.mergeMergeColumns(worksheet, formattedData, mergeColumnHeaders);
      worksheet['!cols'] = this.autofitColumns(formattedData);
      const safeSheetName = sheetName.replaceAll(/[\\/:*?[\]]/g, '-')
        .substring(0, 31);          
      workbook.SheetNames.push(safeSheetName);
      workbook.Sheets[safeSheetName] = worksheet;
    });

    const excelBuffer: any = this.writeWorkbook(workbook);
    this.saveAsExcelFile(excelBuffer, fileName);
  }

  public writeWorkbook(workbook: XLSX.WorkBook): ArrayBuffer {
    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  }

  private wrapAndCenterCell(cell: XLSX.CellObject) {
    const wrapAndCenterCellStyle = { alignment: { wrapText: true, vertical: 'center', horizontal: 'center' } };
    this.setCellStyle(cell, wrapAndCenterCellStyle);
  }

  autofitColumns(dataArr: any) {
    const headers = Object.keys(dataArr[0]);
    return headers.map((header: any) => ({
      wch: Math.max(
        header.length,
        ...dataArr.map((row: any) => row[header] ? (row[header].toString().length + 10) : 0)
      )
    }));
  }

  private setCellStyle(cell: XLSX.CellObject, style: {}) {
    cell.s = style;
  }

  private centerAlignMergeColumns(worksheet: XLSX.WorkSheet, formattedData: Array<Record<string, any>>, mergeColumnHeaders: string[]): void {
    if (!formattedData?.length || !mergeColumnHeaders.length) {
      return;
    }

    const headers = Object.keys(formattedData[0]);
    const mergeColumnIndexes = headers
      .map((header, index) => ({ header, index }))
      .filter((x) => mergeColumnHeaders.includes(x.header))
      .map((x) => x.index);

    if (mergeColumnIndexes.length <= 0) {
      return;
    }

    for (let rowIndex = 1; rowIndex <= formattedData.length; rowIndex += 1) {
      mergeColumnIndexes.forEach((columnIndex) => {
        const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
        const cell = worksheet[address];
        if (!cell) {
          return;
        }
        this.setCellStyle(cell, {
          alignment: {
            horizontal: 'center',
            vertical: 'center'
          }
        });
      });
    }
  }

  private mergeMergeColumns(worksheet: XLSX.WorkSheet, formattedData: Array<Record<string, any>>, mergeColumnHeaders: string[]): void {
    if (!formattedData?.length || !mergeColumnHeaders.length) {
      return;
    }

    const headers = Object.keys(formattedData[0]);
    const mergeColumnIndexes = headers
      .map((header, index) => ({ header, index }))
      .filter((x) => mergeColumnHeaders.includes(x.header))
      .map((x) => x.index);

    if (mergeColumnIndexes.length <= 0) {
      return;
    }

    const merges: XLSX.Range[] = worksheet['!merges'] ?? [];

    mergeColumnIndexes.forEach((columnIndex) => {
      let startRow = 1;

      for (let rowIndex = 2; rowIndex <= formattedData.length; rowIndex += 1) {
        const cellValue = formattedData[rowIndex - 1][headers[columnIndex]];
        const isContinuation = cellValue === '' || cellValue === null || cellValue === undefined;
        if (!isContinuation) {
          if (rowIndex - 1 > startRow) {
            merges.push({ s: { r: startRow, c: columnIndex }, e: { r: rowIndex - 1, c: columnIndex } });
          }
          startRow = rowIndex;
        }
      }
      if (formattedData.length > startRow) {
        merges.push({ s: { r: startRow, c: columnIndex }, e: { r: formattedData.length, c: columnIndex } });
      }
    });

    worksheet['!merges'] = merges;
  }

  getformattedDate() {
    const date = new Date();
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const yy = String(date.getFullYear()).slice(-2);
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${mm}_${dd}_${yy}_${hh}_${mi}_${ss}`;
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    saveAs(data, `${fileName}_export_${this.getformattedDate()}${EXCEL_EXTENSION}`);
  }
}
